import { useState, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { useIsRouteGame } from '@/shared/hooks/useIsRouteGame';

type UseScreenOrientationT = {
  orientation: 'portrait' | 'landscape';
  shouldShow: boolean;
};
type OrientationT = Omit<UseScreenOrientationT, 'shouldShow'>;

const NAME_ORIENTATION = {
  PORTRAIT: 'portrait',
  LANDSCAPE: 'landscape',
};

export const useScreenOrientation = (): UseScreenOrientationT => {
  const { isGame } = useIsRouteGame();
  const mediaQuery = window.matchMedia(
    `(orientation: ${NAME_ORIENTATION.PORTRAIT})`
  );

  const [orientation, setOrientation] = useState<OrientationT>(() => {
    return mediaQuery?.matches
      ? NAME_ORIENTATION.PORTRAIT
      : NAME_ORIENTATION.LANDSCAPE;
  });

  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (!isMobile) return;
      const newOrientation = mediaQuery?.matches
        ? NAME_ORIENTATION.PORTRAIT
        : NAME_ORIENTATION.LANDSCAPE;
      setOrientation(newOrientation);
    };

    const handleOrientationChange = (e: MediaQueryListEvent) => {
      if (!isMobile) return;
      setOrientation(
        e.matches ? NAME_ORIENTATION.PORTRAIT : NAME_ORIENTATION.LANDSCAPE
      );
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    mediaQuery.addEventListener('change', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      mediaQuery.removeEventListener('change', handleOrientationChange);
    };
  }, [isMobile]);

  useEffect(() => {
    const newShouldShow = isGame
      ? false
      : isMobile && orientation === NAME_ORIENTATION.LANDSCAPE;
    setShouldShow(newShouldShow);
  }, [orientation, isGame, isMobile]);

  return { orientation, shouldShow };
};

export default useScreenOrientation;
